import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { IoMdClose } from 'react-icons/io';
import debounce from 'lodash/debounce';
import { toWords } from 'number-to-words';
import CommonSalesQuotationFields from '../CommonSalesQuotationFields';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function PumpDomesticQuotationConversion() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [enquiryrefno, setEnquiryrefno] = useState('');
  const [enquiryrefdate, setEnquiryrefdate] = useState('');
  const [status, setstatus] = useState('');
  const [customermaterialcode, setCustomermaterialcode] = useState('');
  const [customerenquirynumber, setcustomerenquirynumber] = useState('');
  const [quotevalidity, setQuotevalidity] = useState('');
  const [deliveryterm, setDeliveryterm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [commonFormData, setCommonFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Calculation state
  const [packingandforwardingpercent, setPackingandforwardingpercent] = useState(0);
  const [freightpercent, setFreightpercent] = useState(0);
  const [gstpercent, setGstpercent] = useState(18);
  const [calculationSummary, setCalculationSummary] = useState({
    nettotal: 0,
    pAndF: 0,
    freight: 0,
    subtotal: 0,
    gst: 0,
    grandtotalinr: 0
  });

  const unitOptions = [
    { value: "", label: "Select UOM" },
    { value: "bot", label: "Bot" },
    { value: "ctn", label: "Ctn" },
    { value: "days", label: "Days" },
    { value: "ft", label: "Ft" },
    { value: "hrs", label: "Hrs" },
    { value: "kg", label: "Kg" },
    { value: "ltr", label: "Ltr" },
    { value: "mtr", label: "Mtr" },
    { value: "nos", label: "Nos" },
    { value: "pil", label: "Pil" },
    { value: "pkt", label: "Pkt" },
    { value: "set", label: "Set" },
    { value: "pcs", label: "Pcs" }
  ];




  const [quotationno, setquotationno] = useState('');
  const [quotationdate, setquotationdate] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);


  const checkquotationno = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }


    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}quotation/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg('❌ This enquiry number already exists.');
        setIsEnquiryValid(false);
      } else if (response.data === false) {
        setEnquiryValidationMsg('✅ This enquiry number is available.');
        setIsEnquiryValid(true);
      } else {
        setEnquiryValidationMsg('Unexpected response from server.');
        setIsEnquiryValid(false);
      }
    } catch (error) {
      console.error("Error checking enquiry number:", error);
      setEnquiryValidationMsg('❌ Error validating enquiry number.');
      setIsEnquiryValid(false);
    }
  };


  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checkquotationno(quotationno);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [quotationno]);

  // Format numbers with commas
  const formatNumberWithCommas = (num) => {
    if (num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

   const numberToWordsWithRupees = (num) => {
     const amount = Math.round(num);
     const words = toWords(amount);
 
     // Capitalize first letter of each word
     const capitalizedWords = words.split(' ').map(word =>
       word.charAt(0).toUpperCase() + word.slice(1)
     ).join(' ');
 
     return `${capitalizedWords} Rupees Only`;
   };

  // Handle common form data changes
  const handleCommonFormData = (data) => {
    setCommonFormData(data);
    if (data.customerDetails) {
      setFreightpercent(parseFloat(data.customerDetails.freight) || 0);
      setPackingandforwardingpercent(parseFloat(data.customerDetails.packingandforwarding) || 0);
    }
  };

  // Calculate totals whenever rows or percentages change
  useEffect(() => {
    calculateTotals();
  }, [rows, packingandforwardingpercent, freightpercent, gstpercent]);

  const calculateTotals = () => {
    const nettotal = rows.reduce((sum, row) => {
      const quantity = parseFloat(row.quantity) || 0;
      const unitrate = parseFloat(row.unitrate) || 0;
      return sum + (quantity * unitrate);
    }, 0);

    const pAndF = nettotal * (packingandforwardingpercent / 100);
    const freight = nettotal * (freightpercent / 100);
    const subtotal = nettotal + pAndF + freight;
    const gst = subtotal * (gstpercent / 100);
    const grandtotalinr = subtotal + gst;

    setCalculationSummary({
      nettotal,
      pAndF,
      freight,
      subtotal,
      gst,
      grandtotalinr
    });
  };

  // Fetch product data for dropdown
  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(encodeURIComponent(inputValue || " "));
  }, 300);

  const getProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );

      const products = response.data?.Index || response.data?.data || [];
      const mapped = products.map(product => ({
        value: product.id,
        label: `${product.description}  - ${product.serialnumber} - ${product.partno}`,
        description: product.description,
        itemcode: product.itemcode,
        hsnsacno: product.hsnsacno,
        partno: product.partno,
        serialnumber: product.serialnumber,
        modelnumber: product.modelnumber,
        tagnumber: product.tagnumber,
        moc: product.moc,
        uom: product.unitofmeasurement || unitOptions[0].value,
      }));

      setProductOptions(mapped);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    getProductData();
  }, [productSearch]);

  // Handle adding a new product row
  const handleProductChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedProduct(selectedOption);

    const newRow = {
      description: selectedOption.description,
      value: selectedOption.value,
      label: selectedOption.label,
      quantity: '',
      unitrate: '',
      amount: 0,
      partno: selectedOption.partno,
      hsnsacno: selectedOption.hsnsacno,
      itemcode: selectedOption.itemcode,
      serialnumber: selectedOption.serialnumber,
      modelnumber: selectedOption.modelnumber,
      tagnumber: selectedOption.tagnumber,
      moc: selectedOption.moc,
      uom: selectedOption.uom || unitOptions[0].value,
      productremarks: "",
    };

    setRows([...rows, newRow]);
  };

  // Handle changes in row data
  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (key === 'quantity' || key === 'unitrate') {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitrate = parseFloat(updatedRows[index].unitrate) || 0;
      updatedRows[index].amount = quantity * unitrate;
    }

    setRows(updatedRows);
  };

  // Remove a row
  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Fetch existing quotation data
  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}enquiry/findById/${id}`, {
          headers: authHeader()
        });

        const data = response.data;

        // Set form values from fetched data
        setstatus(data.status || '');
        setEnquiryrefno(data.enquiryno || '');
        setEnquiryrefdate(data.enquirydate || '');
        setCustomermaterialcode(data.customermaterialcode || '');
        setcustomerenquirynumber(data.customerenquirynumber || '');
        setQuotevalidity(data.quotevalidity || '');
        setDeliveryterm(data.deliveryterm || '');
        setPackingandforwardingpercent(data.packingandforwardingpercent || 0);
        setFreightpercent(data.freightpercent || 0);
        setGstpercent(data.gstpercent || 18);

        // Prepare common form data
        const commonData = {
          customer: {
            value: data.customer.id,
            label: data.customer.companyname,
          },
          customerDetails: {
            contactno: data.contactno,
            contactperson: data.contactperson,
            email: data.email,
            paymentterm: data.customer?.paymentterm,
            packingandforwarding: data.customer?.packingandforwarding,
            freight: data.customer?.freight
          },
          customerMultipleAddress: {
            value: data.customeraddress.id,
            label: data.customeraddress.customeraddress
          },
        };

        setCommonFormData(commonData);

        // Prepare rows data
        const items = data.enquiryItems.map(item => ({
          description: item.product.description,
          value: item.product.id,
          label: `${item.product.description} - ${item.product.serialnumber}`,
          quantity: item.quantity,
          unitrate: item.unitrate,
          amount: item.amount,
          partno: item.partno,
          hsnsacno: item.hsnsacno,
          itemcode: item.itemcode,
          serialnumber: item.serialnumber,
          modelnumber: item.modelnumber,
          tagnumber: item.tagnumber,
          moc: item.moc,
          uom: item.uom,
          productremarks: item.productremarks
        }));

        setRows(items);
        setIsLoading(false);

      } catch (error) {
        console.error('Error fetching quotation data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load quotation data'
        });
        navigate('/quotationIndexTable/1');
      }
    };

    if (id) {
      fetchQuotationData();
    }
  }, [id, navigate]);

  // Handle form submission
  const handleSubmit = async (e) => {
    setFormSubmitted(true);
    if (!isEnquiryValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid enquiry number before submitting.',
      });
      return;
    }
    e.preventDefault();
    const {
      customer,
      customerDetails,
      customerMultipleAddress,
    } = commonFormData;

    if (!customer || !customerMultipleAddress) {
      Swal.fire({
        icon: 'warning',
        title: 'Please fill the required details!',
        text: 'All fields marked with an asterisk are required.',
      });
      return;
    }

    const quotationItems = rows.map(row => ({
      product: { id: row.value },
      quantity: row.quantity,
      unitrate: row.unitrate,
      amount: row.amount,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      partno: row.partno || '',
      uom: row.uom || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      productremarks: row.productremarks || '',
      moc: row.moc || '',
    }));

    const formData = {
      quotationItems,
      status,
      quotationtype: "Pump Spare",
      quotationcategory: "Domestic",
      customer: { id: customer.value },
      contactno: customerDetails.contactno,
      contactperson: customerDetails.contactperson,
      email: customerDetails.email,
      customeraddress: { id: customerMultipleAddress?.value },
      quotationdate,
      quotationno,
      customermaterialcode,
      customerenquirynumber,
      quotevalidity,
      deliveryterm,
      enquiryrefno,
      enquiryrefdate,
      packingandforwardingpercent,
      freightpercent,
      gstpercent,
      nettotal: calculationSummary.nettotal,
      grandtotalinr: calculationSummary.grandtotalinr,
      freight: customerDetails.freight,
      packingandforwarding: customerDetails.packingandforwarding,
      paymentterm: customerDetails.paymentterm
    };

    try {
      await axios.post(`${BASE_URL}quotation/enquirytoquotation/${id}`, formData, {
        headers: authHeader()
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Enquiry successfully converted to quotation!'
      });
      navigate("/quotationIndexTable/1");
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update quotation.'
      });
    }
  };

  if (isLoading) {
    return <div className="container text-center mt-5">Loading quotation data...</div>;
  }

  return (
    <div className="container">
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Quotation Number: *</Form.Label>
              <Form.Control
                type="text"
                value={quotationno}
                onChange={e => {
                  setquotationno(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid enquiry number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Quotation Date</Form.Label>
              <Form.Control
                type="date"
                value={quotationdate}
                onChange={(e) => setquotationdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Enquiry Ref No.:</Form.Label>
              <Form.Control
                type="text"
                value={enquiryrefno}
                onChange={(e) => setEnquiryrefno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquirydate">
              <Form.Label>Enquiry Ref Date</Form.Label>
              <Form.Control
                type="date"
                value={enquiryrefdate}
                onChange={(e) => setEnquiryrefdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Customer Enquiry Number:</Form.Label>
              <Form.Control
                type="text"
                value={customerenquirynumber}
                onChange={(e) => setcustomerenquirynumber(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="customermaterialcode">
              <Form.Label>Customer Material code</Form.Label>
              <Form.Control
                type="text"
                value={customermaterialcode}
                onChange={(e) => setCustomermaterialcode(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonSalesQuotationFields
          onDataChange={handleCommonFormData}
          initialData={commonFormData}
        />

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="quotevalidity">
              <Form.Label>Quote Validity</Form.Label>
              <Form.Control
                type="text"
                value={quotevalidity}
                onChange={(e) => setQuotevalidity(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="deliveryterm">
              <Form.Label>Delivery Term</Form.Label>
              <Form.Control
                type="text"
                value={deliveryterm}
                onChange={(e) => setDeliveryterm(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="productSelect" className="mb-3">
          <Form.Label>Add Products</Form.Label>
          <Select
            value={selectedProduct}
            onInputChange={handleProductSearchChange}
            onChange={handleProductChange}
            options={productOptions}
            placeholder="Search or select product"
            isClearable
          />
        </Form.Group>

        <div className="table-responsive mt-3">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Description</th>
                <th>Serial No.</th>
                <th>Model No.</th>
                <th>Tag No.</th>
                <th>Part No.</th>
                <th>MOC</th>
                <th>HSN Code</th>
                <th>Product Code</th>
                <th>Quantity</th>
                <th>UOM</th>
                <th>Unit Rate</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {row.description}
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={row.productremarks}
                      onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                      placeholder="Remarks"
                    />
                  </td>
                  <td>{row.serialnumber}</td>
                  <td>{row.modelnumber}</td>
                  <td>{row.tagnumber}</td>
                  <td>{row.partno}</td>
                  <td>{row.moc}</td>
                  <td>{row.hsnsacno}</td>
                  <td>{row.itemcode}</td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.quantity}
                      onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                    />
                  </td>
                  <td>
                    <Select
                      className='uom'
                      value={unitOptions.find(option => option.value === row.uom)}
                      onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                      options={unitOptions}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.unitrate}
                      onChange={(e) => handleRowChange(index, 'unitrate', e.target.value)}
                    />
                  </td>
                  <td>{formatNumberWithCommas(row.amount)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeRow(index)}
                    >
                      <IoMdClose />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <br />
        <hr />

        <div className="table-responsive mt-3 mb-4">
          <h4>Amount Calculation</h4>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Description</th>
                <th>Percentage</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Net Total :</td>
                <td>-</td>
                <td>{formatNumberWithCommas(calculationSummary.nettotal)}</td>
              </tr>
              <tr>
                <td>Packing & Forwarding % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={packingandforwardingpercent}
                    onChange={(e) => setPackingandforwardingpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                  {commonFormData.customerDetails?.packingandforwarding &&
                    <small className="text-muted"> (From customer: {commonFormData.customerDetails.packingandforwarding}%)</small>
                  }
                </td>
                <td>{formatNumberWithCommas(calculationSummary.pAndF)}</td>
              </tr>
              <tr>
                <td>Freight % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={freightpercent}
                    onChange={(e) => setFreightpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                  {commonFormData.customerDetails?.freight &&
                    <small className="text-muted"> (From customer: {commonFormData.customerDetails.freight}%)</small>
                  }
                </td>
                <td>{formatNumberWithCommas(calculationSummary.freight)}</td>
              </tr>
              <tr>
                <td>Sub Total :</td>
                <td>-</td>
                <td>{formatNumberWithCommas(calculationSummary.subtotal)}</td>
              </tr>
              <tr>
                <td>GST % :</td>
                <td>
                  <Form.Control
                    type="number"
                    value={gstpercent}
                    onChange={(e) => setGstpercent(e.target.value)}
                    style={{ width: '80px' }}
                  />
                </td>
                <td>{formatNumberWithCommas(calculationSummary.gst)}</td>
              </tr>
              <tr>
                <td><strong>Grand Total :</strong></td>
                <td>-</td>
                <td><strong>{formatNumberWithCommas(calculationSummary.grandtotalinr)}</strong></td>
              </tr>
              <tr>
                <td colSpan="2"><strong>Grand Total in Words :</strong></td>
                <td><em>{numberToWordsWithRupees(calculationSummary.grandtotalinr)}</em></td>
              </tr>
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-between mb-4">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate(-1)}
          >
            CANCEL
          </button>
          <button className="btn btn-primary" type="submit">
            CONVERT QUOTATION
          </button>
        </div>
      </Form>
    </div>
  );
}

export default PumpDomesticQuotationConversion;

